import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Paper,
} from '@mui/material'
import {
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Webhook as WebhookIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'

const NotificationSettings = ({ settings = {}, onChange, isLoading = false }) => {
  const [localSettings, setLocalSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    webhookNotifications: true,
    
    // Email settings
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    smtpSecure: true,
    emailFromAddress: 'noreply@virtualsms.com',
    emailFromName: 'VirtualSMS Admin',
    
    // SMS settings
    smsProvider: 'twilio',
    smsApiKey: '',
    smsApiSecret: '',
    smsFromNumber: '',
    
    // Webhook settings
    webhookUrl: '',
    webhookSecret: '',
    webhookTimeout: 30,
    webhookRetries: 3,
    
    // Notification types
    notifyOnUserRegistration: true,
    notifyOnSystemErrors: true,
    notifyOnHighUsage: true,
    notifyOnSecurityEvents: true,
    notifyOnPaymentEvents: true,
    notifyOnProviderIssues: true,
    
    // Notification frequency
    errorNotificationFrequency: 'immediate',
    reportFrequency: 'daily',
    summaryFrequency: 'weekly',
    
    // Threshold settings
    highUsageThreshold: 80,
    lowBalanceThreshold: 100,
    errorRateThreshold: 5,
    
    ...settings
  })

  const handleSettingChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }))
    onChange?.()
  }

  const notificationTypes = [
    {
      key: 'notifyOnUserRegistration',
      icon: <InfoIcon />,
      title: 'Kullanıcı Kayıtları',
      description: 'Yeni kullanıcı kayıtları için bildirim',
      color: 'info'
    },
    {
      key: 'notifyOnSystemErrors',
      icon: <ErrorIcon />,
      title: 'Sistem Hataları',
      description: 'Kritik sistem hataları için bildirim',
      color: 'error'
    },
    {
      key: 'notifyOnHighUsage',
      icon: <WarningIcon />,
      title: 'Yüksek Kullanım',
      description: 'Sistem kaynaklarında yüksek kullanım uyarısı',
      color: 'warning'
    },
    {
      key: 'notifyOnSecurityEvents',
      icon: <SecurityIcon />,
      title: 'Güvenlik Olayları',
      description: 'Şüpheli aktiviteler ve güvenlik ihlalleri',
      color: 'error'
    },
    {
      key: 'notifyOnPaymentEvents',
      icon: <InfoIcon />,
      title: 'Ödeme Olayları',
      description: 'Ödeme işlemleri ve bakiye değişiklikleri',
      color: 'success'
    },
    {
      key: 'notifyOnProviderIssues',
      icon: <WarningIcon />,
      title: 'Provider Sorunları',
      description: 'SMS provider bağlantı sorunları',
      color: 'warning'
    }
  ]

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Bildirim Ayarları
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Sistem bildirimleri ve uyarı ayarlarını yapılandırın
        </Typography>
      </Box>

      {/* Notification Channels */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon />
            Bildirim Kanalları
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon />
                    <Typography>E-posta Bildirimleri</Typography>
                  </Box>
                }
                secondary="Admin e-posta adresleri üzerinden bildirim gönder"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.emailNotifications}
                  onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                  color="primary"
                />
              </ListItemSecondaryAction>
            </ListItem>
            
            <Divider />
            
            <ListItem>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmsIcon />
                    <Typography>SMS Bildirimleri</Typography>
                  </Box>
                }
                secondary="Kritik durumlar için SMS ile anında bildirim"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.smsNotifications}
                  onChange={(e) => handleSettingChange('smsNotifications', e.target.checked)}
                  color="info"
                />
              </ListItemSecondaryAction>
            </ListItem>
            
            <Divider />
            
            <ListItem>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WebhookIcon />
                    <Typography>Webhook Bildirimleri</Typography>
                  </Box>
                }
                secondary="Harici sistemlere HTTP webhook ile bildirim"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.webhookNotifications}
                  onChange={(e) => handleSettingChange('webhookNotifications', e.target.checked)}
                  color="success"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Email Configuration */}
      {localSettings.emailNotifications && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon />
              E-posta Yapılandırması
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMTP Sunucusu"
                  value={localSettings.smtpHost}
                  onChange={(e) => handleSettingChange('smtpHost', e.target.value)}
                  helperText="E-posta gönderimi için SMTP sunucu adresi"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMTP Port"
                  type="number"
                  value={localSettings.smtpPort}
                  onChange={(e) => handleSettingChange('smtpPort', parseInt(e.target.value) || 587)}
                  helperText="SMTP sunucu portu (genellikle 587 veya 465)"
                  inputProps={{ min: 1, max: 65535 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMTP Kullanıcı Adı"
                  value={localSettings.smtpUsername}
                  onChange={(e) => handleSettingChange('smtpUsername', e.target.value)}
                  helperText="E-posta hesabı kullanıcı adı"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMTP Şifre"
                  type="password"
                  value={localSettings.smtpPassword}
                  onChange={(e) => handleSettingChange('smtpPassword', e.target.value)}
                  helperText="E-posta hesabı şifresi"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Gönderen E-posta"
                  value={localSettings.emailFromAddress}
                  onChange={(e) => handleSettingChange('emailFromAddress', e.target.value)}
                  helperText="Bildirim e-postalarının gönderileceği adres"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Gönderen Adı"
                  value={localSettings.emailFromName}
                  onChange={(e) => handleSettingChange('emailFromName', e.target.value)}
                  helperText="E-posta gönderenin görünen adı"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localSettings.smtpSecure}
                      onChange={(e) => handleSettingChange('smtpSecure', e.target.checked)}
                    />
                  }
                  label="Güvenli Bağlantı (TLS/SSL)"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* SMS Configuration */}
      {localSettings.smsNotifications && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmsIcon />
              SMS Yapılandırması
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>SMS Sağlayıcı</InputLabel>
                  <Select
                    value={localSettings.smsProvider}
                    label="SMS Sağlayıcı"
                    onChange={(e) => handleSettingChange('smsProvider', e.target.value)}
                  >
                    <MenuItem value="twilio">Twilio</MenuItem>
                    <MenuItem value="nexmo">Vonage (Nexmo)</MenuItem>
                    <MenuItem value="aws">AWS SNS</MenuItem>
                    <MenuItem value="custom">Özel API</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Gönderen Numara"
                  value={localSettings.smsFromNumber}
                  onChange={(e) => handleSettingChange('smsFromNumber', e.target.value)}
                  helperText="SMS'lerin gönderileceği telefon numarası"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="API Anahtarı"
                  value={localSettings.smsApiKey}
                  onChange={(e) => handleSettingChange('smsApiKey', e.target.value)}
                  helperText="SMS sağlayıcı API anahtarı"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="API Secret"
                  type="password"
                  value={localSettings.smsApiSecret}
                  onChange={(e) => handleSettingChange('smsApiSecret', e.target.value)}
                  helperText="SMS sağlayıcı secret anahtarı"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Webhook Configuration */}
      {localSettings.webhookNotifications && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WebhookIcon />
              Webhook Yapılandırması
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Webhook URL"
                  value={localSettings.webhookUrl}
                  onChange={(e) => handleSettingChange('webhookUrl', e.target.value)}
                  helperText="Bildirimlerin gönderileceği webhook endpoint'i"
                  placeholder="https://api.example.com/webhooks/notifications"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Webhook Secret"
                  type="password"
                  value={localSettings.webhookSecret}
                  onChange={(e) => handleSettingChange('webhookSecret', e.target.value)}
                  helperText="Webhook imzalama için kullanılacak secret"
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Timeout (saniye)"
                  type="number"
                  value={localSettings.webhookTimeout}
                  onChange={(e) => handleSettingChange('webhookTimeout', parseInt(e.target.value) || 30)}
                  helperText="HTTP isteği zaman aşımı"
                  inputProps={{ min: 5, max: 300 }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Tekrar Denemeler"
                  type="number"
                  value={localSettings.webhookRetries}
                  onChange={(e) => handleSettingChange('webhookRetries', parseInt(e.target.value) || 3)}
                  helperText="Başarısız durumda tekrar deneme sayısı"
                  inputProps={{ min: 0, max: 10 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Notification Types */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Bildirim Türleri
          </Typography>
          
          <Grid container spacing={2}>
            {notificationTypes.map((type) => (
              <Grid item xs={12} md={6} key={type.key}>
                <Paper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                      <Box sx={{ color: `${type.color}.main` }}>
                        {type.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {type.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {type.description}
                        </Typography>
                      </Box>
                    </Box>
                    <Switch
                      checked={localSettings[type.key]}
                      onChange={(e) => handleSettingChange(type.key, e.target.checked)}
                      color={type.color}
                      size="small"
                    />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Frequency Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon />
            Bildirim Sıklığı
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Hata Bildirimleri</InputLabel>
                <Select
                  value={localSettings.errorNotificationFrequency}
                  label="Hata Bildirimleri"
                  onChange={(e) => handleSettingChange('errorNotificationFrequency', e.target.value)}
                >
                  <MenuItem value="immediate">Anında</MenuItem>
                  <MenuItem value="hourly">Saatlik</MenuItem>
                  <MenuItem value="daily">Günlük</MenuItem>
                  <MenuItem value="disabled">Devre Dışı</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Rapor Sıklığı</InputLabel>
                <Select
                  value={localSettings.reportFrequency}
                  label="Rapor Sıklığı"
                  onChange={(e) => handleSettingChange('reportFrequency', e.target.value)}
                >
                  <MenuItem value="hourly">Saatlik</MenuItem>
                  <MenuItem value="daily">Günlük</MenuItem>
                  <MenuItem value="weekly">Haftalık</MenuItem>
                  <MenuItem value="monthly">Aylık</MenuItem>
                  <MenuItem value="disabled">Devre Dışı</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Özet Raporları</InputLabel>
                <Select
                  value={localSettings.summaryFrequency}
                  label="Özet Raporları"
                  onChange={(e) => handleSettingChange('summaryFrequency', e.target.value)}
                >
                  <MenuItem value="daily">Günlük</MenuItem>
                  <MenuItem value="weekly">Haftalık</MenuItem>
                  <MenuItem value="monthly">Aylık</MenuItem>
                  <MenuItem value="disabled">Devre Dışı</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Threshold Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Eşik Değerleri
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Yüksek Kullanım Eşiği (%)"
                type="number"
                value={localSettings.highUsageThreshold}
                onChange={(e) => handleSettingChange('highUsageThreshold', parseInt(e.target.value) || 80)}
                helperText="CPU/Memory kullanım uyarı eşiği"
                inputProps={{ min: 50, max: 95 }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Düşük Bakiye Eşiği"
                type="number"
                value={localSettings.lowBalanceThreshold}
                onChange={(e) => handleSettingChange('lowBalanceThreshold', parseInt(e.target.value) || 100)}
                helperText="Kullanıcı bakiye uyarı eşiği"
                inputProps={{ min: 10, max: 1000 }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Hata Oranı Eşiği (%)"
                type="number"
                value={localSettings.errorRateThreshold}
                onChange={(e) => handleSettingChange('errorRateThreshold', parseInt(e.target.value) || 5)}
                helperText="API hata oranı uyarı eşiği"
                inputProps={{ min: 1, max: 50 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Test Notifications */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Test Bildirimleri
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Yapılandırılan bildirim kanallarını test etmek için aşağıdaki butonları kullanabilirsiniz.
          </Alert>
          
          <Grid container spacing={2}>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                disabled={!localSettings.emailNotifications}
              >
                E-posta Testi
              </Button>
            </Grid>
            
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<SmsIcon />}
                disabled={!localSettings.smsNotifications}
              >
                SMS Testi
              </Button>
            </Grid>
            
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<WebhookIcon />}
                disabled={!localSettings.webhookNotifications}
              >
                Webhook Testi
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}

export default NotificationSettings